import { type ComponentType, ReactNode, useState } from 'react';

import {
  Link,
  type LinkProps,
  Stack,
  type StackProps,
  Tooltip,
  TooltipProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import cx from 'classnames';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import styles from './IconedExternalLink.module.scss';
import { Icon, IconProps } from '@/components/ui/Icon';
import { uiIcons } from '@/constants/uiIcons';

const MotionLink = motion.create(Link as ComponentType<LinkProps>);
const MotionStack = motion.create(Stack as ComponentType<StackProps>);

const iconVariants = {
  rest: { scale: 1, z: 0 },
  hover: { scale: 1.1, z: 0 },
};

interface IconedExternalLinkProps {
  text?: string;
  href: string;
  typographyProps?: TypographyProps;
  iconSize?: IconProps['size'];
  tooltip?: ReactNode;
  tooltipProps?: Omit<TooltipProps, 'title'>;
}

export const IconedExternalLink = ({
  text,
  href,
  typographyProps,
  iconSize = 'tiny',
  tooltip,
  tooltipProps,
}: IconedExternalLinkProps) => {
  const { t } = useTranslation('ui');
  const [hovered, setHovered] = useState(false);

  const content = (
    <MotionLink
      className={styles.Link}
      href={href}
      target="_blank"
      rel="noreferrer"
      underline="none" // Instead animate it through styling
      initial="rest"
      whileHover="hover"
      animate="rest"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-automation-id={`iconed-external-link__${(text || t('ReadMore')).replace(/\s+/g, '_')}`}
    >
      {/* irregular gap value to make spacing look more like a regular regular text space */}
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography
          variant={typographyProps?.variant || 'label'}
          {...typographyProps}
          className={cx(styles.Typography, typographyProps?.className, {
            [styles.Hovered]: hovered,
          })}
        >
          {text || t('ReadMore')}
        </Typography>
        <MotionStack
          variants={iconVariants}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          style={{ willChange: 'transform' }}
        >
          <Icon
            name={uiIcons.navigation.openExternal}
            color={typographyProps?.color ?? 'inherit'}
            size={iconSize}
          />
        </MotionStack>
      </Stack>
    </MotionLink>
  );

  return tooltip ? (
    <Tooltip {...tooltipProps} title={tooltip}>
      {content}
    </Tooltip>
  ) : (
    content
  );
};
